'use client';

import { Badge } from '@repo/ui/components/ui/badge';
import { Button } from '@repo/ui/components/ui/button';
import { Card, CardContent } from '@repo/ui/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@repo/ui/components/ui/dialog';
import { Label } from '@repo/ui/components/ui/label';
import { Edit, Eye, Minus, Plus, RotateCw, Upload, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';

interface ImageUploadProps {
  name: string;
  label: string;
  value?: string;
  onChange: (name: string, value: string | File) => void;
  onRotate?: (fieldName: string, rotation: number) => Promise<void>;
  required?: boolean;
  imageConfig?: {
    maxFiles?: number;
    acceptedTypes?: string[];
    maxSize?: number;
  };
  error?: string;
  isLoading?: boolean;
  /** Species image shown when no user upload (visual only, not persisted) */
  placeholderSrc?: string;
}

export function ImageUpload({
  name,
  label,
  value,
  onChange,
  onRotate,
  required = false,
  imageConfig = {},
  error,
  isLoading = false,
  placeholderSrc,
}: ImageUploadProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isRotating, setIsRotating] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalZoom, setModalZoom] = useState(1);
  const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    maxFiles = 1,
    acceptedTypes = ['image/jpeg', 'image/png', 'image/webp'],
    maxSize = 20
  } = imageConfig;

  // Cleanup effect for object URLs
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;

    setIsUploading(true);
    setUploadError(null);

    const file = acceptedFiles[0];
    if (!file) return;

    // Validate file size
    if (file.size > maxSize * 1024 * 1024) {
      setUploadError(`Le fichier est trop volumineux. Taille maximum: ${maxSize}MB`);
      setIsUploading(false);
      return;
    }

    // Validate file type
    if (!acceptedTypes.includes(file.type)) {
      setUploadError('Type de fichier non supporté');
      setIsUploading(false);
      return;
    }

    // Create immediate preview URL
    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);

    // Pass the File object to onChange for proper handling by parent components
    onChange(name, file);
    setIsUploading(false);
  }, [name, onChange, maxSize, acceptedTypes]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: acceptedTypes.reduce((acc, type) => ({ ...acc, [type]: [] }), {}),
    maxFiles,
    disabled: isUploading
  });

  const handleRemove = () => {
    // Cleanup object URL to prevent memory leaks
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }

    onChange(name, '');
    setUploadError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleModify = () => {
    fileInputRef.current?.click();
  };

  const handleRotate = async () => {
    if (!onRotate) {
      // No rotation handler provided
      return;
    }

    setIsRotating(true);
    try {
      // Always rotate by 90 degrees clockwise
      await onRotate(name, 90);

      // Force image reload by adding cache-busting parameter
      // This ensures the browser fetches the newly rotated image
      if (value && typeof value === 'string') {
        const url = new URL(value, window.location.origin);
        url.searchParams.set('_t', Date.now().toString());
        // Trigger a re-render by updating the display URL
        // The parent component should update the value prop with the new URL
        // For now, we'll force a reload by clearing and resetting previewUrl if it exists
        if (previewUrl) {
          URL.revokeObjectURL(previewUrl);
          setPreviewUrl(null);
        }
      }
    } catch (error) {
      console.error('Error rotating image:', error);
      setUploadError('Erreur lors de la rotation de l\'image');
    } finally {
      setIsRotating(false);
    }
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
    setModalZoom(1);
    setModalPosition({ x: 0, y: 0 });
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setModalZoom(1);
    setModalPosition({ x: 0, y: 0 });
  };

  const handleZoomIn = () => {
    setModalZoom(prev => Math.min(prev * 1.5, 5));
  };

  const handleZoomOut = () => {
    setModalZoom(prev => Math.max(prev / 1.5, 0.5));
  };

  const handleResetZoom = () => {
    setModalZoom(1);
    setModalPosition({ x: 0, y: 0 });
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (modalZoom > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - modalPosition.x, y: e.clientY - modalPosition.y });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging && modalZoom > 1) {
      setModalPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const hasImage = previewUrl || (value && value.trim() !== '');

  // Add cache-busting parameter to image URL to force reload after rotation
  const getDisplayUrl = () => {
    if (previewUrl) return previewUrl;
    if (!value || typeof value !== 'string') return value;

    // If it's already a URL with cache-busting, use it as is
    // Otherwise, add a timestamp to force reload
    try {
      // Check if value is already a full URL
      let url: URL;
      if (value.startsWith('http://') || value.startsWith('https://')) {
        url = new URL(value);
      } else {
        url = new URL(value, window.location.origin);
      }

      // Only add cache-busting if not already present
      if (!url.searchParams.has('_t')) {
        url.searchParams.set('_t', Date.now().toString());
      }
      return url.toString();
    } catch {
      // If value is not a valid URL, return as is
      return value;
    }
  };

  const displayUrl = getDisplayUrl();

  return (
    <div className="space-y-3">
      <Label className="text-sm font-medium">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </Label>

      {!hasImage ? (
        <Card
          {...getRootProps()}
          className={`
            cursor-pointer transition-all duration-200 border-2 border-dashed
            ${isDragActive
              ? 'border-primary bg-primary/5'
              : 'border-border hover:border-primary/50 hover:bg-muted/50'
            }
            ${error ? 'border-red-500' : ''}
          `}
        >
          <CardContent className={placeholderSrc && !isUploading ? 'p-0' : 'p-6'}>
            <div className="flex flex-col items-center justify-center space-y-3">
              {isUploading ? (
                <>
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
                  <p className="text-sm text-muted-foreground">Téléchargement en cours...</p>
                </>
              ) : placeholderSrc ? (
                <div className="relative w-full h-48">
                  <img
                    src={placeholderSrc}
                    alt="Image de l'espèce"
                    className="w-full h-full object-cover rounded-md opacity-60"
                  />
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/20 rounded-md">
                    <Badge variant="secondary" className="bg-background/90 text-foreground">
                      Image de l&apos;espèce par défaut
                    </Badge>
                    <p className="text-xs text-white px-4 text-center">
                      {isDragActive ? 'Déposez l\'image ici' : 'Cliquez pour ajouter votre propre photo'}
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  <Upload className="h-8 w-8 text-muted-foreground" />
                  <div className="text-center">
                    <p className="text-sm font-medium">
                      {isDragActive ? 'Déposez l\'image ici' : 'Cliquez ou déposez une image'}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      PNG, JPG, WEBP jusqu'à {maxSize}MB
                    </p>
                  </div>
                </>
              )}
            </div>
          </CardContent>
          <input {...getInputProps()} />
        </Card>
      ) : (
        <div className="space-y-3">
          <div className="relative group">
            <img
              src={displayUrl}
              alt={label}
              className="w-full h-48 object-cover rounded-lg border border-border"
            />

            {/* Action buttons overlay */}
            <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                className="h-8 w-8 p-0 bg-background/90 hover:bg-background"
                onClick={handleOpenModal}
                title="Aperçu avec zoom"
              >
                <Eye className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                className="h-8 w-8 p-0 bg-background/90 hover:bg-background"
                onClick={handleModify}
                title="Modifier"
              >
                <Edit className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                className="h-8 w-8 p-0 bg-background/90 hover:bg-background"
                onClick={handleRotate}
                disabled={isRotating || isLoading}
                title="Pivoter"
              >
                <RotateCw className={`h-4 w-4 ${isRotating ? 'animate-spin' : ''}`} />
              </Button>
              <Button
                type="button"
                variant="destructive"
                size="sm"
                className="h-8 w-8 p-0 bg-background/90 hover:bg-background"
                onClick={handleRemove}
                title="Supprimer"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Bottom action buttons */}
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleModify}
              disabled={isUploading}
              className="flex-1"
            >
              <Edit className="h-4 w-4 mr-2" />
              Modifier
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleRotate}
              disabled={isUploading || isRotating || isLoading}
              className="flex-1"
            >
              <RotateCw className={`h-4 w-4 mr-2 ${isRotating ? 'animate-spin' : ''}`} />
              {isRotating ? 'Rotation...' : 'Pivoter'}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleRemove}
              disabled={isUploading}
              className="flex-1"
            >
              <X className="h-4 w-4 mr-2" />
              Supprimer
            </Button>
          </div>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept={acceptedTypes.join(',')}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) {
            onDrop([file]);
          }
        }}
        className="hidden"
      />

      {uploadError && (
        <p className="text-sm text-red-500">{uploadError}</p>
      )}

      {error && (
        <p className="text-sm text-red-500">{error}</p>
      )}

      {/* Image Preview Modal */}
      <Dialog open={isModalOpen} onOpenChange={handleCloseModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] p-0">
          <DialogHeader className="p-6 pb-2">
            <DialogTitle>{label} - Aperçu avec zoom</DialogTitle>
          </DialogHeader>

          <div className="relative flex-1 overflow-hidden">
            {/* Zoom Controls */}
            <div className="absolute top-4 left-4 z-10 flex gap-2">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={handleZoomOut}
                disabled={modalZoom <= 0.5}
                title="Zoom arrière"
              >
                <Minus className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={handleResetZoom}
                title="Réinitialiser le zoom"
              >
                <Eye className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={handleZoomIn}
                disabled={modalZoom >= 5}
                title="Zoom avant"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>

            {/* Zoom level indicator */}
            <div className="absolute top-4 right-4 z-10">
              <div className="bg-background/90 px-2 py-1 rounded text-sm">
                {Math.round(modalZoom * 100)}%
              </div>
            </div>

            {/* Image Container */}
            <div
              className="w-full h-[70vh] overflow-hidden bg-muted flex items-center justify-center relative"
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              style={{ cursor: modalZoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default' }}
            >
              <img
                src={displayUrl}
                alt={label}
                className="max-w-none transition-transform duration-200"
                style={{
                  transform: `scale(${modalZoom}) translate(${modalPosition.x / modalZoom}px, ${modalPosition.y / modalZoom}px)`,
                  transformOrigin: 'center center'
                }}
                draggable={false}
              />
            </div>

            {/* Instructions */}
            {modalZoom > 1 && (
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-10">
                <div className="bg-background/90 px-3 py-1 rounded text-sm text-muted-foreground">
                  Cliquez et glissez pour déplacer l'image
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
